import { AuthModel } from './_models'

const AUTH_LOCAL_STORAGE_KEY = 'kt-auth-react-v'
let isLoggingOut = false

const getAuth = (): AuthModel | undefined => {
  if (!localStorage) {
    return
  }

  const lsValue: string | null = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY)
  if (!lsValue) {
    return
  }

  try {
    const auth: AuthModel = JSON.parse(lsValue) as AuthModel
    if (auth) {
      // You can easily check auth_token expiration also
      return auth
    }
  } catch (error) {
    console.error('AUTH LOCAL STORAGE PARSE ERROR', error)
  }
}

const setAuth = (auth: AuthModel) => {
  if (!localStorage) {
    return
  }

  try {
    const lsValue = JSON.stringify(auth)
    localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, lsValue)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE SAVE ERROR', error)
  }
}

const removeAuth = () => {
  if (!localStorage) {
    return
  }

  try {
    localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE REMOVE ERROR', error)
  }
}

const LOGOUT_REASON_KEY = 'authLogoutReason'

// The page is hard-reloaded on logout, which wipes the console. Keep the reason
// in sessionStorage so the login page can show why the user was sent back.
const describeError = (error: any) => {
  const response = error?.response
  const parts = [
    error?.config?.url ? `url=${error.config.url}` : '',
    response?.status ? `status=${response.status}` : 'no response (network/CORS/mixed-content?)',
    error?.message ? `message=${error.message}` : '',
  ]
  return parts.filter(Boolean).join(' | ')
}

const getLogoutReason = () => {
  try {
    return sessionStorage.getItem(LOGOUT_REASON_KEY) || undefined
  } catch (error) {
    return undefined
  }
}

const clearLogoutReason = () => {
  try {
    sessionStorage.removeItem(LOGOUT_REASON_KEY)
  } catch (error) {
    // ignore
  }
}

const logoutSystem = (reason?: string) => {
  if (isLoggingOut) {
    return
  }

  isLoggingOut = true
  if (reason) {
    console.error('Logging out:', reason)
    try {
      sessionStorage.setItem(LOGOUT_REASON_KEY, reason)
    } catch (error) {
      // ignore
    }
  }
  localStorage.removeItem('authTokenLoop')
  localStorage.removeItem('WidgetAccess')
  localStorage.removeItem('suppliers')
  localStorage.removeItem('user')
  removeAuth()
  window.location.replace('/auth')
}

const shouldSkipAuthLogout = (config?: { url?: string }) => {
  const url = (config?.url || '').toLowerCase()
  return url.includes('bi_dashboard_login') || url.includes('/login')
}

const getResponseMessage = (data?: { message?: string; error?: string; msg?: string }) => {
  return String(data?.message || data?.error || data?.msg || '').toLowerCase()
}

const AUTH_FAILURE_PATTERNS = [
  'auth fail',
  'auth failed',
  'auth failure',
  'expired',
  'unauthorized',
  'unauthenticated',
  'invalid token',
  'token expired',
  'auth expired',
  'session expired',
  'authentication fail',
]

const normalizeResponseData = (data: any) => {
  if (typeof data === 'string') {
    try {
      return JSON.parse(data)
    } catch (error) {
      return { msg: data }
    }
  }

  return data
}

const isAuthFailureMessage = (message: string) => {
  return AUTH_FAILURE_PATTERNS.some((pattern) => message.includes(pattern))
}

export const isAuthExpiredResponse = (response?: { status?: number; data?: any }) => {
  if (!response) {
    return false
  }

  const status = response.status
  if (status === 401 || status === 403) {
    return true
  }

  const data = normalizeResponseData(response.data)
  if (!data) {
    return false
  }

  const success = data.success
  const isFailure = success === false || success === 'false' || success === 0
  if (!isFailure) {
    return false
  }

  return isAuthFailureMessage(getResponseMessage(data))
}

export const enforceLogoutIfExpired = (response?: { status?: number; data?: any }) => {
  if (!localStorage.getItem('authTokenLoop')) {
    return false
  }

  if (!isAuthExpiredResponse(response)) {
    return false
  }

  logoutSystem(
    `Session rejected by API (status=${response?.status}, message=${getResponseMessage(
      normalizeResponseData(response?.data)
    )})`
  )
  return true
}

const clearAxiosInterceptors = (axiosInstance: any) => {
  if (axiosInstance?.interceptors?.request?.handlers) {
    axiosInstance.interceptors.request.handlers = []
  }

  if (axiosInstance?.interceptors?.response?.handlers) {
    axiosInstance.interceptors.response.handlers = []
  }
}

export function configureAxiosAuth(axiosInstance: any) {
  clearAxiosInterceptors(axiosInstance)

  axiosInstance.defaults.headers.Accept = 'application/json'
  axiosInstance.interceptors.request.use(
    (config: { headers: { Authorization: string } }) => {
      return config
    },
    (err: any) => Promise.reject(err)
  )

  axiosInstance.interceptors.response.use(
    (response: any) => {
      if (
        !shouldSkipAuthLogout(response.config) &&
        enforceLogoutIfExpired(response)
      ) {
        return Promise.reject(new Error('Session expired'))
      }

      return response
    },
    (error: any) => {
      if (
        !shouldSkipAuthLogout(error?.config) &&
        enforceLogoutIfExpired(error?.response)
      ) {
        return Promise.reject(error)
      }

      return Promise.reject(error)
    }
  )
}

export function setupAxios(axiosInstance: any) {
  configureAxiosAuth(axiosInstance)
}

export {
  getAuth,
  setAuth,
  removeAuth,
  logoutSystem,
  describeError,
  getLogoutReason,
  clearLogoutReason,
  AUTH_LOCAL_STORAGE_KEY,
}
